import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppIcon, InfoModal, PageHeader, useContainerQuery } from '@backrs_ui/common-ui';

import gradSchoolGuideHeroImage from '@backrs_ui/grow-ui/public/images/grad_school_guide_hero.png';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import exploreMajors from '@backrs_ui/grow-ui/src/assets/images/majors/explore-majors.png';
import majorsColleges from '@backrs_ui/grow-ui/src/assets/images/majors/majors-colleges.png';

import { Box, Button, Typography, SxProps, Theme, LinearProgress } from '@mui/material';

import { getIcon } from './components';
import { useAgentContext } from './GradSchoolGuidePageContainer.agentContext';
import { gradSchoolGuideContent } from '../content';
import type { DegreeType, FrameworkCategory, BackrsFeature, FAQItem } from '../content/types';

// Map tool routes to their image assets
const toolImageMap: Record<string, string> = {
  '/majors/explorer': majorsColleges,
  '/search/majors': exploreMajors,
};

// Framework color mapping using design system tokens
const frameworkColorTokens: Record<FrameworkCategory['color'], string> = {
  green: 'Success/500',
  yellow: 'Warning/400',
  orange: 'Orange/500',
  red: 'Error/500',
};

type ActiveModal = 'degreeTypes' | 'framework' | null;

/**
 * Grad School Guide Page Container
 * Graduate school guide with hero section, modals, and FAQ
 */
export const GradSchoolGuidePageContainer: React.FC = () => {
  const navigate = useNavigate();

  useAgentContext();

  const [selectedFaqTab, setSelectedFaqTab] = useState<string>(gradSchoolGuideContent.faq[0].id);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [containerRef, isSidebarHidden] = useContainerQuery(900);

  const handleFaqTabClick = (faqId: string) => {
    setSelectedFaqTab(faqId);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
  };

  const handleToolCardClick = (route: string) => {
    navigate(route);
  };

  const renderJumpMenu = () => (
    <Box sx={jumpMenuSidebarSx}>
      <Typography variant="text-sm-semibold" sx={jumpToLabelSx}>
        {gradSchoolGuideContent.appStrings.jumpMenu.label}
      </Typography>
      <Box sx={tabsContainerSx}>
        {/* Individual FAQ tabs */}
        {gradSchoolGuideContent.faq.map((faq: FAQItem) => (
          <Box
            key={faq.id}
            sx={getTabSx(selectedFaqTab === faq.id)}
            onClick={() => handleFaqTabClick(faq.id)}
          >
            <Typography variant="text-md-semibold" sx={getTabTextSx(selectedFaqTab === faq.id)}>
              {faq.question}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box ref={containerRef} sx={outerContainerSx}>
      {/* PageHeader */}
      <PageHeader
        title={gradSchoolGuideContent.appStrings.pageHeader.title}
        subtitle={gradSchoolGuideContent.appStrings.pageHeader.subtitle}
        rightComponents={[
          <Button
            id="grad-school-back-to-majors-btn"
            key="back"
            variant="contained"
            size="md"
            hierarchy="Primary"
            startIcon={<AppIcon iconName="ArrowLeft" diameter="20px" color="currentColor" />}
            onClick={() => navigate(APP_ROUTE.MAJORS)}
          >
            {gradSchoolGuideContent.appStrings.pageHeader.backToMajors}
          </Button>,
        ]}
        maxWidth={1200}
        showBorder={true}
      />

      <Box sx={containerSx}>
        {/* Hero Section */}
        <Box sx={heroSectionSx}>
          <Box sx={heroContentSx}>
            <Box sx={heroTextSx}>
              <Typography variant="display-xs-semibold" sx={heroTitleSx}>
                {gradSchoolGuideContent.hero.title}
              </Typography>
              <Typography variant="body1" sx={heroQuoteSx}>
                {gradSchoolGuideContent.banner.text}
              </Typography>
              <Typography variant="body1" sx={heroSubtitleSx}>
                <strong>{gradSchoolGuideContent.hero.subtitle}</strong>
              </Typography>
            </Box>
            <Box sx={heroImageCardSx}>
              <Box sx={heroImageWrapperSx}>
                <Box
                  component="img"
                  src={gradSchoolGuideHeroImage}
                  alt={gradSchoolGuideContent.appStrings.heroSection.imageAlt}
                  sx={heroImageSx}
                />
              </Box>
              {/* Footer Section */}
              <Box sx={heroFooterSx}>
                <Box sx={heroFooterContentSx}>
                  <Typography variant="text-md-medium" sx={heroFooterTextSx}>
                    Know your options before you apply
                  </Typography>
                  <Button
                    id="grad-school-degree-types-btn"
                    variant="contained"
                    size="md"
                    hierarchy="Secondary gray"
                    endIcon={
                      <AppIcon iconName="ArrowUpRight" diameter="20px" color="currentColor" />
                    }
                    onClick={() => setActiveModal('degreeTypes')}
                  >
                    {gradSchoolGuideContent.appStrings.heroSection.degreeTypesButton}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* How Backrs Helps Section */}
        <Box sx={sectionContainerSx}>
          <Box sx={sectionHeaderRowSx}>
            <Typography variant="text-lg-semibold">
              {gradSchoolGuideContent.backrsHelps.title}
            </Typography>
            <Button
              id="grad-school-framework-btn"
              variant="contained"
              size="md"
              hierarchy="Secondary gray"
              startIcon={<AppIcon iconName="InfoCircle" diameter="20px" color="currentColor" />}
              onClick={() => setActiveModal('framework')}
            >
              {gradSchoolGuideContent.appStrings.heroSection.frameworkButton}
            </Button>
          </Box>
          <Box sx={dividerSx} />

          <Box sx={featuresGridSx}>
            {gradSchoolGuideContent.backrsHelps.features.map(
              (feature: BackrsFeature, index: number) => (
                <Box key={index} sx={featureCardSx}>
                  <Box sx={featureCardContentSx}>
                    <Box sx={featureIconSx}>{getIcon(feature.icon, 28)}</Box>
                    <Typography variant="body2" sx={featureDescriptionSx}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              ),
            )}
          </Box>
        </Box>

        {/* Explore Tools Section */}
        <Box sx={sectionContainerSx}>
          <Box sx={toolsGridSx}>
            {gradSchoolGuideContent.exploreTools.map((tool, index) => {
              const toolImage = toolImageMap[tool.route];

              return (
                <Box key={index} sx={toolCardSx} onClick={() => handleToolCardClick(tool.route)}>
                  {toolImage && (
                    <Box
                      component="img"
                      src={toolImage}
                      alt={`${tool.title} illustration`}
                      sx={toolCardImageSx}
                    />
                  )}
                  <Box sx={toolCardContentSx}>
                    <Box sx={toolCardHeaderSx}>
                      <Typography variant="text-lg-semibold" sx={toolCardTitleSx}>
                        {tool.route === '/majors/explorer' ? (
                          <>
                            {gradSchoolGuideContent.appStrings.toolCards.majorsCareersSwitchTitle}{' '}
                            <AppIcon
                              iconName="SwitchHorizontal01"
                              diameter="16px"
                              color="currentColor"
                            />{' '}
                            {
                              gradSchoolGuideContent.appStrings.toolCards
                                .majorsCareersSwitchSeparator
                            }
                          </>
                        ) : (
                          tool.title
                        )}
                      </Typography>
                      <Box sx={toolIconSx}>{getIcon(tool.icon, 24)}</Box>
                    </Box>
                    <Typography variant="text-sm-regular" sx={toolCardDescSx}>
                      {tool.route === '/search/majors'
                        ? gradSchoolGuideContent.appStrings.toolCards.exploreMajorsDescription
                        : tool.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* FAQ Section - Last */}
        <Box id="faq-section" sx={faqSectionSx}>
          <Box sx={faqHeaderContainerSx}>
            <Typography variant="text-lg-semibold">
              {gradSchoolGuideContent.appStrings.faqSection.title}
            </Typography>
            <Typography variant="body1" sx={faqSubtitleSx}>
              {gradSchoolGuideContent.appStrings.faqSection.subtitle}
            </Typography>
          </Box>
          <Box sx={faqDividerSx} />
          <Box sx={faqContentContainerSx}>
            {/* Jump Menu - hidden on mobile */}
            {renderJumpMenu()}

            {/* FAQ Items */}
            <Box sx={faqItemsContainerSx}>
              {gradSchoolGuideContent.faq
                .filter((faq) => isSidebarHidden || faq.id === selectedFaqTab)
                .map((faq: FAQItem) => (
                  <Box key={faq.id} sx={faqItemContainerSx}>
                    <Typography variant="display-sm-semibold" sx={faqQuestionSx}>
                      {faq.question}
                    </Typography>
                    <Typography variant="body1" sx={faqAnswerSx}>
                      {faq.answer}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Degree Types Modal */}
      <InfoModal
        open={activeModal === 'degreeTypes'}
        onClose={handleCloseModal}
        width="md"
        featuredIcon={{
          iconName: 'GraduationHat02',
          size: 'lg',
          color: 'Colors/Foreground/fg-brand-primary',
        }}
        headerContent={
          <Box sx={modalHeaderSx}>
            <Typography variant="text-lg-semibold">
              {gradSchoolGuideContent.appStrings.degreeTypesModal.title}
            </Typography>
            <Typography variant="body2" sx={modalSubtitleSx}>
              {gradSchoolGuideContent.appStrings.degreeTypesModal.subtitle}
            </Typography>
          </Box>
        }
        bodyContent={
          <Box sx={modalContentSx}>
            {/* Undergraduate Degrees */}
            <Box sx={modalSectionSx}>
              <Typography variant="text-md-semibold" sx={modalSectionTitleSx}>
                {gradSchoolGuideContent.appStrings.degreeTypesModal.undergraduateTitle}
              </Typography>
              <Box sx={degreeCardsListSx}>
                {gradSchoolGuideContent.degreeTypes.undergraduate.map(
                  (degree: DegreeType, index: number) => (
                    <Box key={index} sx={degreeCardSx}>
                      <Box sx={degreeIconCircleSx('undergraduate')}>
                        <AppIcon iconName="GraduationHat02" diameter="20px" color="Brand/700" />
                      </Box>
                      <Box sx={degreeCardContentSx}>
                        <Box sx={degreeCardHeaderSx}>
                          <Typography variant="text-md-semibold" sx={degreeCardTitleSx}>
                            {degree.name}
                          </Typography>
                          <Box sx={degreeBadgeSx('undergraduate')}>
                            <Typography
                              variant="text-xs-medium"
                              sx={degreeBadgeTextSx('undergraduate')}
                            >
                              {degree.duration}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={degreeCardDescSx}>
                          {degree.description}
                        </Typography>
                      </Box>
                    </Box>
                  ),
                )}
              </Box>
            </Box>

            {/* Graduate Degrees */}
            <Box sx={modalSectionSx}>
              <Typography variant="text-md-semibold" sx={modalSectionTitleSx}>
                {gradSchoolGuideContent.appStrings.degreeTypesModal.graduateTitle}
              </Typography>
              <Box sx={degreeCardsListSx}>
                {gradSchoolGuideContent.degreeTypes.graduate.map(
                  (degree: DegreeType, index: number) => (
                    <Box key={index} sx={degreeCardSx}>
                      <Box sx={degreeIconCircleSx('graduate')}>
                        <AppIcon iconName="GraduationHat02" diameter="20px" color="Success/700" />
                      </Box>
                      <Box sx={degreeCardContentSx}>
                        <Box sx={degreeCardHeaderSx}>
                          <Typography variant="text-md-semibold" sx={degreeCardTitleSx}>
                            {degree.name}
                          </Typography>
                          <Box sx={degreeBadgeSx('graduate')}>
                            <Typography variant="text-xs-medium" sx={degreeBadgeTextSx('graduate')}>
                              {degree.duration}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={degreeCardDescSx}>
                          {degree.description}
                        </Typography>
                        {degree.examples && degree.examples.length > 0 && (
                          <Typography variant="text-sm-regular" sx={degreeCardExamplesSx}>
                            {gradSchoolGuideContent.appStrings.degreeTypesModal.examplesPrefix}{' '}
                            {degree.examples.join(', ')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ),
                )}
              </Box>
            </Box>
          </Box>
        }
      />

      {/* Framework Modal */}
      <InfoModal
        open={activeModal === 'framework'}
        onClose={handleCloseModal}
        width="md"
        featuredIcon={{
          iconName: 'Target04',
          size: 'lg',
          color: 'Colors/Foreground/fg-brand-primary',
        }}
        headerContent={
          <Box sx={modalHeaderSx}>
            <Typography variant="text-lg-semibold">
              {gradSchoolGuideContent.appStrings.frameworkModal.title}
            </Typography>
            <Typography variant="body2" sx={modalSubtitleSx}>
              {gradSchoolGuideContent.framework.description}
            </Typography>
          </Box>
        }
        bodyContent={
          <Box sx={modalContentSx}>
            <Box sx={frameworkBarsContainerSx}>
              {gradSchoolGuideContent.framework.categories.map(
                (category: FrameworkCategory, index: number) => (
                  <Box key={index} sx={frameworkBarItemSx}>
                    <Box sx={frameworkBarHeaderSx}>
                      <Typography variant="text-sm-semibold" sx={frameworkCategoryLabelSx}>
                        {category.category}
                      </Typography>
                      <Typography variant="text-sm-semibold" sx={frameworkPercentageSx}>
                        {category.percentage}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={category.percentage}
                      sx={getProgressBarWithColorSx(category.color)}
                    />
                    <Typography variant="body2" sx={frameworkCategoryDescriptionSx}>
                      {category.description}
                    </Typography>
                    <Typography variant="text-sm-regular" sx={frameworkExamplesSx}>
                      {gradSchoolGuideContent.appStrings.frameworkModal.examplesPrefix}{' '}
                      {category.examples.join(', ')}
                    </Typography>
                  </Box>
                ),
              )}
            </Box>

            <Box sx={takeawayCardSx}>
              <Box sx={takeawayIconCircleSx}>
                <AppIcon iconName="Lightbulb05" diameter="20px" color="Colors/Base/White" />
              </Box>
              <Box sx={takeawayTextContainerSx}>
                <Typography sx={takeawayLabelSx}>
                  {gradSchoolGuideContent.appStrings.frameworkModal.keyTakeawayLabel}
                </Typography>
                <Typography sx={takeawayTextSx}>
                  {gradSchoolGuideContent.framework.takeaway}
                </Typography>
              </Box>
            </Box>
          </Box>
        }
      />
    </Box>
  );
};

// Styles
const outerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
  width: '100%',
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
});

// Hero Section Styles
const heroSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const heroContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap-reverse',
  gap: backrs.spacing['spacing-xl'],
  alignItems: 'stretch',
  '@container (min-width: 900px)': {
    gap: backrs.spacing['spacing-3xl'],
  },
});

const heroTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  flex: '1 1 min(400px, 100%)',
  minWidth: 0,
});

const heroTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const heroQuoteSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const heroSubtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const heroImageCardSx: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 min(400px, 100%)',
  minWidth: 0,
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: '#b42318',
});

const heroImageWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'hidden',
  backgroundColor: '#b42318',
  aspectRatio: '3 / 2',
  borderTopLeftRadius: `calc(${backrs.radius['radius-xl']} - 1px)`,
  borderTopRightRadius: `calc(${backrs.radius['radius-xl']} - 1px)`,
});

const heroImageSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  display: 'block',
  objectFit: 'cover',
};

const heroFooterSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const heroFooterContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  px: backrs.spacing['spacing-xl'],
  py: backrs.spacing['spacing-lg'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  '& .MuiButton-root': {
    whiteSpace: 'nowrap',
  },
});

const heroFooterTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

// Section Styles
const sectionContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const sectionHeaderRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '1px',
  backgroundColor: backrs.colors['Colors/Border/border-secondary'],
  mt: backrs.spacing['spacing-xl'],
  mb: backrs.spacing['spacing-3xl'],
});

// Features Section
const featuresGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(250px, 100%), 1fr))',
  gap: backrs.spacing['spacing-xl'],
});

const featureCardSx: SxProps<Theme> = ({ backrs }) => ({
  p: backrs.spacing['spacing-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-lg'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const featureCardContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  textAlign: 'center',
});

const featureIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  color: backrs.colors['Colors/Foreground/fg-brand-primary'],
});

const featureDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

// Tools Section
const toolsGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
  gap: backrs.spacing['spacing-xl'],
});

const toolCardSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  overflow: 'hidden',
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-lg'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
  '&:hover': {
    boxShadow: backrs.shadows['shadow-lg'],
    transform: 'translateY(-2px)',
  },
});

const toolCardImageSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: backrs.spacing['spacing-10xl'],
  '@container (min-width: 900px)': {
    height: backrs.spacing['spacing-11xl'],
  },
  objectFit: 'cover',
  borderTopLeftRadius: backrs.radius['radius-md'],
  borderTopRightRadius: backrs.radius['radius-md'],
});

const toolCardContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  p: backrs.spacing['spacing-md'],
});

const toolCardHeaderSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const toolIconSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Foreground/fg-brand-primary'],
  flexShrink: 0,
});

const toolCardTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const toolCardDescSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

// Jump menu sidebar styles
const jumpMenuSidebarSx: SxProps<Theme> = ({ backrs }) => ({
  width: backrs.width['width-xxs'],
  flexShrink: 0,
  display: 'none',
  '@container (min-width: 900px)': {
    display: 'flex',
  },
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const jumpToLabelSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-semibold'],
  color: backrs.colors['Colors/Text/text-brand-primary'],
});

const tabsContainerSx: SxProps<Theme> = () => ({
  display: 'flex',
  flexDirection: 'column',
});

const getTabSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-md'],
    px: backrs.spacing['spacing-lg'],
    py: backrs.spacing['spacing-md'],
    cursor: 'pointer',
    borderLeft: isActive
      ? `2px solid ${backrs.colors['Colors/Border/border-brand']}`
      : '2px solid transparent',
    '&:hover': {
      backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
    },
  });

const getTabTextSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs, typography }) => ({
    ...typography['text-md-semibold'],
    color: isActive
      ? backrs.colors['Colors/Text/text-brand-primary']
      : backrs.colors['Colors/Text/text-quaternary'],
  });

// FAQ Section Styles
const faqSectionSx: SxProps<Theme> = () => ({
  display: 'flex',
  flexDirection: 'column',
});

const faqHeaderContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const faqSubtitleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-md-regular'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const faqDividerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '1px',
  backgroundColor: backrs.colors['Colors/Border/border-secondary'],
  mt: backrs.spacing['spacing-xl'],
});

const faqContentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-4xl'],
  mt: backrs.spacing['spacing-xl'],
});

const faqItemsContainerSx: SxProps<Theme> = () => ({
  flex: 1,
  minWidth: 0,
});

const faqItemContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  py: backrs.spacing['spacing-xl'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  '&:first-of-type': {
    borderTop: 'none',
  },
});

const faqQuestionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const faqAnswerSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

// Modal Styles
const modalHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const modalSubtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const modalContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const modalSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const modalSectionTitleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-xs-medium'],
  color: backrs.colors['Colors/Text/text-tertiary'],
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
});

// Degree Card Styles (relevance-level pattern)
const degreeCardsListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const degreeCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
  p: backrs.spacing['spacing-lg'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const degreeIconCircleSx =
  (type: 'undergraduate' | 'graduate'): SxProps<Theme> =>
  ({ backrs }) => ({
    width: backrs.spacing['spacing-5xl'],
    height: backrs.spacing['spacing-5xl'],
    minWidth: backrs.spacing['spacing-5xl'],
    borderRadius: backrs.radius['radius-full'],
    backgroundColor:
      type === 'undergraduate' ? backrs.colors['Brand/100'] : backrs.colors['Success/100'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

const degreeCardContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  flex: 1,
});

const degreeCardHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  flexWrap: 'wrap',
});

const degreeCardTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const degreeBadgeSx =
  (type: 'undergraduate' | 'graduate'): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-xs'],
    px: backrs.spacing['spacing-sm'],
    py: backrs.spacing['spacing-xxs'],
    borderRadius: backrs.radius['radius-full'],
    backgroundColor:
      type === 'undergraduate' ? backrs.colors['Brand/50'] : backrs.colors['Success/50'],
    '&::before': {
      content: '""',
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      backgroundColor:
        type === 'undergraduate' ? backrs.colors['Brand/500'] : backrs.colors['Success/500'],
    },
  });

const degreeBadgeTextSx =
  (type: 'undergraduate' | 'graduate'): SxProps<Theme> =>
  ({ backrs }) => ({
    color: type === 'undergraduate' ? backrs.colors['Brand/700'] : backrs.colors['Success/700'],
  });

const degreeCardDescSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const degreeCardExamplesSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

// Framework Styles (for modal)
const frameworkBarsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const frameworkBarItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const frameworkBarHeaderSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const frameworkCategoryLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const frameworkPercentageSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const getProgressBarWithColorSx =
  (color: FrameworkCategory['color']): SxProps<Theme> =>
  ({ backrs }) => ({
    height: backrs.spacing['spacing-lg'],
    borderRadius: backrs.radius['radius-sm'],
    backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
    '& .MuiLinearProgress-bar': {
      backgroundColor: backrs.colors[frameworkColorTokens[color] as keyof typeof backrs.colors],
    },
  });

const frameworkCategoryDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const frameworkExamplesSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const takeawayCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  p: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const takeawayIconCircleSx: SxProps<Theme> = ({ backrs }) => ({
  width: backrs.spacing['spacing-5xl'],
  height: backrs.spacing['spacing-5xl'],
  minWidth: backrs.spacing['spacing-5xl'],
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: backrs.colors['Brand/500'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const takeawayTextContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  flex: 1,
});

const takeawayLabelSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-md-bold'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

const takeawayTextSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-regular'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});
